import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { axiosInterceptor } from 'services'
import { API_BASE_URL } from 'services/constants'
import routes from 'constants/routes'
import { Loader2, Copy, Check } from 'lucide-react'

interface TrialResult {
	magicUrl: string
	emailed: boolean
	seed?: { seeded: boolean; locations: number; projects: number; portfolios: number }
	trialDays: number
}

/**
 * Admin-only tool to create a passwordless trial/marketing account. Creates the
 * user, seeds demo data, and emails a magic link — then surfaces the link here
 * so it can be copied even if email delivery is unavailable.
 */
const CreateTrialUserCN = () => {
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)

	const [name, setName] = useState('')
	const [email, setEmail] = useState('')
	const [templateUserId, setTemplateUserId] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [result, setResult] = useState<TrialResult | null>(null)
	const [copied, setCopied] = useState(false)

	// Backend enforces isAdmin; this is a client-side guard for a clean UX.
	if (!userInfo?.isAdmin) {
		return <Navigate to={routes.HOME} replace />
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)
		setResult(null)
		if (!name.trim() || !email.trim()) {
			setError('Name and email are required.')
			return
		}
		setSubmitting(true)
		try {
			const api = axiosInterceptor(API_BASE_URL)
			const { data } = await api.post('/users/trial', {
				name: name.trim(),
				email: email.trim(),
				...(templateUserId.trim() && { templateUserId: templateUserId.trim() }),
			})
			setResult(data)
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || 'Failed to create trial user.')
		} finally {
			setSubmitting(false)
		}
	}

	const copyLink = async () => {
		if (!result?.magicUrl) return
		try {
			await navigator.clipboard.writeText(result.magicUrl)
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		} catch {
			/* clipboard unavailable — user can select manually */
		}
	}

	return (
		<div className="mx-auto max-w-2xl px-4 py-8">
			<h1 className="mb-1 text-2xl font-bold text-gray-900">Create trial account</h1>
			<p className="mb-6 text-sm text-gray-600">
				Creates a passwordless preview account preloaded with sample data and emails the prospect a
				magic link. They get {result?.trialDays ?? 14} days before they must convert.
			</p>

			<form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
				<div className="space-y-2">
					<label htmlFor="t-name" className="text-sm font-medium text-gray-700">
						Name
					</label>
					<input
						id="t-name"
						value={name}
						onChange={e => setName(e.target.value)}
						className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
						placeholder="Prospect's full name"
					/>
				</div>
				<div className="space-y-2">
					<label htmlFor="t-email" className="text-sm font-medium text-gray-700">
						Email
					</label>
					<input
						id="t-email"
						type="email"
						value={email}
						onChange={e => setEmail(e.target.value)}
						className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
						placeholder="prospect@company.com"
					/>
				</div>
				<div className="space-y-2">
					<label htmlFor="t-template" className="text-sm font-medium text-gray-700">
						Template account ID <span className="text-gray-400">(optional)</span>
					</label>
					<input
						id="t-template"
						value={templateUserId}
						onChange={e => setTemplateUserId(e.target.value)}
						className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
						placeholder="Defaults to DEMO_TEMPLATE_USER_ID"
					/>
					<p className="text-xs text-gray-500">
						The account whose locations/projects/portfolios are cloned into the new preview.
					</p>
				</div>

				{error && <p className="text-sm text-destructive">{error}</p>}

				<button
					type="submit"
					disabled={submitting}
					className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-60"
				>
					{submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
					Create trial account
				</button>
			</form>

			{result && (
				<div className="mt-6 rounded-xl border border-success/40 bg-success/5 p-6">
					<h2 className="mb-2 text-base font-semibold text-gray-900">Trial account created</h2>
					<p className="mb-3 text-sm text-gray-600">
						{result.emailed ? 'A magic link was emailed to the prospect.' : 'Email delivery was unavailable — copy the link below and send it manually.'}
						{result.seed?.seeded
							? ` Seeded ${result.seed.locations} locations, ${result.seed.projects} projects, ${result.seed.portfolios} portfolios.`
							: ' No demo data was seeded (no template configured).'}
					</p>
					<div className="flex items-center gap-2">
						<input
							readOnly
							value={result.magicUrl}
							className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-gray-700"
							onFocus={e => e.currentTarget.select()}
						/>
						<button
							type="button"
							onClick={copyLink}
							className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
						>
							{copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
							{copied ? 'Copied' : 'Copy'}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

export default CreateTrialUserCN
